'use client';

import { toast } from '@findy-code/yuffie/ui/toast';
import { gaSendEvent } from '@findy-code/yuffie/utils/events/event';
import { getReferrer } from '@findy-code/yuffie/utils/history/route-history';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';

import type { FetchJobDescriptionDetailFragment } from './graphql';
import {
  useCreateUserLikeMutation,
  useFetchJobDescriptionDataForCurrentUserQuery,
  useFetchJobDescriptionDetailV2CurrentUserQuery,
  useFetchJobDescriptionQuery,
  useKeepJobDescriptionLikeMutation,
  useMarkJobDescriptionAsReadMutation,
} from './graphql';
import type { HandleClickLikeParams } from './types';

type Props = {
  isLogin: boolean;
  jdHash: string;
  mailReferrer: string;
  jobDescription: FetchJobDescriptionDetailFragment | null;
};

export const useCompanyJobDescriptionDetailV2Facade = ({
  isLogin,
  jdHash,
  mailReferrer,
  jobDescription,
}: Props) => {
  const pathname = usePathname() ?? '';
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isOpenedMatchModal, setIsOpenedMatchModal] = useState<boolean>(false);
  const [isFirstLoaded, setIsFirstLoaded] = useState<boolean>(false);

  const [markAsReadJobDescription] = useMarkJobDescriptionAsReadMutation();
  const [createUserLike] = useCreateUserLikeMutation();
  const [keepJobDescriptionLike] = useKeepJobDescriptionLikeMutation();

  // 閲覧中のユーザデータの取得
  const {
    data: jobDescriptionDetailCurrentUser,
    loading: isLoadingCurrentUser,
  } = useFetchJobDescriptionDetailV2CurrentUserQuery({
    skip: !isLogin,
    onCompleted: (data) => {
      if (data.currentUser.id && jobDescription) {
        markAsReadJobDescription({
          variables: { input: { jdHash: jobDescription.jdHash } },
        });
      }
    },
  });
  const currentUser = jobDescriptionDetailCurrentUser?.currentUser;

  // 求人に紐付くいいね、いいかも、マッチのデータを取得
  const {
    data: jobDescriptionForCurrentUserData,
    loading: isLoadingJobDescriptionForCurrentUser,
  } = useFetchJobDescriptionDataForCurrentUserQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isLogin,
    variables: {
      jdHash,
    },
    onCompleted: () => {
      setIsFirstLoaded(true);
    },
  });

  const { data: jobDescriptionData, loading: isLoadingJobDescriptionData } =
    useFetchJobDescriptionQuery({
      skip: !isLogin || !!jobDescription,
      variables: {
        jdHash,
      },
    });

  const jobDescriptionDataForRender = useMemo(() => {
    if (jobDescription) return jobDescription;
    if (!jobDescription && !isLoadingJobDescriptionData && jobDescriptionData)
      return jobDescriptionData.jobDescription;

    return undefined;
  }, [jobDescription, jobDescriptionData, isLoadingJobDescriptionData]);

  const jobDescriptionLike = useMemo(() => {
    return (
      jobDescriptionForCurrentUserData?.jobDescription.jobDescriptionLike ??
      null
    );
  }, [jobDescriptionForCurrentUserData]);

  const userLike = useMemo(() => {
    return jobDescriptionForCurrentUserData?.jobDescription.userLike ?? null;
  }, [jobDescriptionForCurrentUserData]);

  const match = useMemo(() => {
    return jobDescriptionForCurrentUserData?.jobDescription.match ?? null;
  }, [jobDescriptionForCurrentUserData]);

  // 自己申告スキルに登録されたデータのidリスト
  const userSelfRecommendSkillIds = useMemo(
    () => currentUser?.selfRecommendedSkills?.map((skill) => skill.id) || [],
    [currentUser?.selfRecommendedSkills]
  );

  // GitHub解析の結果で登録されたSkillのidリスト
  const userSkillIds = useMemo(
    () =>
      currentUser?.latestGithubAnalysisResult?.analysisResultSkillScores?.map(
        (analysisResultSkillScore) => analysisResultSkillScore.skillId
      ) || [],
    [currentUser?.latestGithubAnalysisResult?.analysisResultSkillScores]
  );

  // ユーザが趣向性に登録しているidリスト
  const userPersonalityIds = useMemo(
    () =>
      currentUser?.characterPersonalities?.map(
        (personality) => personality.answer.id
      ) || [],
    [currentUser?.characterPersonalities]
  );

  // リンククリック時にgaSendEventを発火
  const handleClickLink = useCallback((label: string) => {
    gaSendEvent({
      category: 'link',
      action: 'click',
      label,
    });
  }, []);

  // いいかもボタンクリック時に発火
  const handleClickLike = useCallback(
    async ({
      jdHash,
      jdId,
      isLikedFromUser,
      onErrorCallback,
    }: HandleClickLikeParams) => {
      // すでにいいかもをしている場合は処理を終了
      if (isLikedFromUser === true) {
        onErrorCallback();
        return;
      }

      setIsSubmitting(true);

      await createUserLike({
        fetchPolicy: 'network-only',
        variables: {
          input: {
            jdHash,
            referrer: mailReferrer || getReferrer(),
            likeFrom: 'JOB_DESCRIPTION_DETAIL',
          },
        },
        onCompleted: (data) => {
          if (data.createUserLike?.userLike === undefined) {
            onErrorCallback();
            toast.error('「いいかも」が出来ませんでした。');
            return;
          }
          gaSendEvent({
            category: 'like',
            action: 'click',
            stringValue: JSON.stringify({
              jobDescriptionId: jdId,
              pathname,
            }),
            userId: currentUser?.id,
          });
          if (data.createUserLike.match) {
            setIsOpenedMatchModal(true);
          }
        },
        onError: () => {
          onErrorCallback();
          toast.error('「いいかも」が出来ませんでした。');
        },
      });

      setIsSubmitting(false);
    },
    [createUserLike, currentUser?.id, pathname, mailReferrer]
  );

  // 見送りボタンクリック時に発火
  const handleClickKeep = useCallback(
    async (jdLikeHash: string, isKeep: boolean) => {
      if (isKeep === true) return;
      setIsSubmitting(true);

      await keepJobDescriptionLike({
        fetchPolicy: 'network-only',
        variables: {
          input: {
            jdLikeHash,
          },
        },
        onCompleted: (data) => {
          if (data.keepJobDescriptionLike?.jobDescriptionLike === undefined) {
            toast.error('「今回は見送る」が出来ませんでした。');
          }
          gaSendEvent({
            category: 'keep',
            action: 'click',
            stringValue: JSON.stringify({
              jobDescriptionId: jobDescription?.id,
              pathname,
            }),
            userId: currentUser?.id,
          });
        },
        onError: () => {
          toast.error('「今回は見送る」が出来ませんでした。');
        },
      });
      setIsSubmitting(false);
    },
    [currentUser?.id, jobDescription?.id, keepJobDescriptionLike, pathname]
  );

  // 企業をブロックしているか否かの判定
  const isBlockCompany = useMemo(() => {
    if (!currentUser?.blockCompanies || !jobDescription?.company) return false;
    return currentUser.blockCompanies.some(
      (blockCompany) => blockCompany.id === jobDescription.company.id
    );
  }, [currentUser?.blockCompanies, jobDescription?.company]);

  // 面談確約か否かの判定
  const isUserSuccessCommitmentJobInterview = useMemo(() => {
    if (!jobDescriptionLike) return false;
    // ユーザサクセスプラスに同意履歴を取得 sort順が不明なので一旦最後のものを取得している
    const histories = currentUser?.userSuccessTermAcceptanceHistories;
    const latestUserSuccessPlusAcceptanceHistory = histories
      ? histories[histories.length - 1]
      : undefined;

    // ユーザサクセスプラスに同意していて、面談確約の求人票の場合にtrueを返す
    return (
      latestUserSuccessPlusAcceptanceHistory?.decision === 'ACCEPT' &&
      !!jobDescriptionLike.isUserSuccessCommitmentJobInterview
    );
  }, [jobDescriptionLike, currentUser?.userSuccessTermAcceptanceHistories]);

  return {
    isLoading: isSubmitting,
    isLoadingCurrentUser,
    isLoadingJobDescriptionForCurrentUser,
    isLoadingJobDescriptionData,
    isFirstLoaded,
    isBlockCompany,
    isOpenedMatchModal,
    isUserSuccessCommitmentJobInterview,
    currentUser,
    userSelfRecommendSkillIds,
    userSkillIds,
    userPersonalityIds,
    jobDescriptionDataForRender,
    jobDescriptionLike,
    userLike,
    match,
    handleClickLink,
    handleClickLike,
    handleClickKeep,
    handleCloseMatchModal: () => setIsOpenedMatchModal(false),
  } as const;
};
